// 261015 P4b-1 (Bước 5) — thẻ "Giám khảo" ở đầu khung tập ⑤. CHỈ ĐỌC: không nút sửa/xem film-body/trước-sau (chốt sau Dev review Q7–Q17); chi tiết biên lai nằm trong khối gập.
// Mọi chữ + quyết định (đạt / còn K lỗi / không nhận / chưa kiểm) do `criticReceiptView.ts` (thuần, có jest) — file này chỉ vẽ. Không có biên lai v2 ⇒ không render gì.
import React from 'react';
import { Collapse, Typography } from 'antd';
import type { EpisodeScriptRecord, LocalizeTrace } from '../../../types/adaptation';
import { Pill } from './WbSlot';
import { criticCardOf, type CriticFindingView } from './criticReceiptView';
import { traceLineOf } from './localizeTraceView';
import LocalizeTraceNote from './LocalizeTraceNote';

const { Text } = Typography;

const TONE_PILL: Record<'ok' | 'warn' | 'unknown', 'ok' | 'warn' | 'todo'> = { ok: 'ok', warn: 'warn', unknown: 'todo' };
const PILL_LABEL: Record<'PASSED' | 'REPAIRED' | 'REPAIR_REJECTED' | 'UNRESOLVED' | 'UNCHECKED', string> = {
  PASSED: 'Giám khảo đã chấm', REPAIRED: 'Đã sửa + chấm lại', REPAIR_REJECTED: 'Không nhận bản sửa', UNRESOLVED: 'Còn lỗi nặng', UNCHECKED: 'Chưa kiểm được',
};

const FindingList: React.FC<{ items: ReadonlyArray<CriticFindingView>; testId?: string; extra?: (f: CriticFindingView) => React.ReactNode }> = ({ items, testId, extra }) => (
  <ul style={{ margin: '4px 0 8px', paddingLeft: 18 }} data-testid={testId}>
    {items.map((f, i) => (
      <li key={i} style={{ fontSize: 13 }}>
        <Text strong type="secondary">{f.where} · {f.severityLabel} · {f.codeLabel}</Text>
        <Text type="secondary"> — {f.message}</Text>
        {f.evidence ? <Text type="secondary" italic>{` “${f.evidence}”`}</Text> : null}
        {extra?.(f)}
      </li>
    ))}
  </ul>
);

interface CardProps {
  script: Pick<EpisodeScriptRecord, 'script' | 'violations' | 'criticFreshness'> | null | undefined;
  /** 261023 E3-P3h4d1 — biên nhận bước ③ lúc viết (BE `localizeTrace`); vắng (BE cũ) ⇒ thẻ y như trước. */
  trace?: LocalizeTrace | null;
  onOpenStage?: () => void;
  onOpenScene?: (sceneId: string) => void;
  /** 3k-c — mở sẵn «Chi tiết biên lai» (danh sách lỗi giám khảo) khi tập còn lỗi nặng; mặc định gập (Sổ duyệt / tập sạch như cũ). */
  openDetails?: boolean;
}

const CriticReceiptCard: React.FC<CardProps> = ({ script, trace, onOpenStage, onOpenScene, openDetails = false }) => {
  const card = criticCardOf(script);
  if (!card) return null;
  const d = card.details;
  // 3h4d1: mỗi lỗi CÒN LẠI có dòng «lúc viết cảnh này nhận tên nào ở bước ③» + đường mở đúng ③ / cảnh (chỉ đọc, không tự sửa).
  const traceExtra = (f: CriticFindingView): React.ReactNode => {
    const line = traceLineOf(trace, f.sceneId);
    if (!line) return null;
    const sid = f.sceneId;
    return <LocalizeTraceNote line={line} testId="critic-finding-trace" onOpenStage={onOpenStage} onOpenScene={sid && onOpenScene ? () => onOpenScene(sid) : undefined} />;
  };
  const blocks: React.ReactNode[] = [];
  if (d.firstPass.length) blocks.push(<div key="first"><Text strong style={{ fontSize: 13 }}>Giám khảo thấy ở lượt chấm đầu ({d.firstPass.length})</Text><FindingList items={d.firstPass} /></div>);
  if (card.status === 'REPAIRED' || d.remaining.length) blocks.push(<div key="rem"><Text strong style={{ fontSize: 13 }}>Còn lại sau lượt chấm cuối ({d.remaining.length})</Text>{d.remaining.length ? <FindingList items={d.remaining} extra={traceExtra} /> : <div><Text type="secondary" style={{ fontSize: 13 }}>Không còn ý kiến nào.</Text></div>}</div>);
  if (d.targetScenes.length) blocks.push(<div key="rep" style={{ fontSize: 13 }}><Text type="secondary">Cảnh gửi đi sửa: {d.targetScenes.join(', ')}{d.changedScenes.length ? ` · đã đổi: ${d.changedScenes.join(', ')}` : ''}{d.skippedScenes.length ? ` · chưa sửa (vượt giới hạn 3 cảnh): ${d.skippedScenes.join(', ')}` : ''}</Text></div>);
  // 3e (L13): chuỗi vòng máy tự sửa + lý do dừng (chỉ có ở biên lai của nhánh chuỗi).
  if (d.rounds.length || d.chainStop) {
    blocks.push(
      <div key="chain" data-testid="critic-receipt-chain">
        <Text strong style={{ fontSize: 13 }}>Máy tự sửa{d.rounds.length ? ` (${d.rounds.length} vòng)` : ''}</Text>
        {d.rounds.length > 0 && <ul style={{ margin: '4px 0 8px', paddingLeft: 18 }}>{d.rounds.map((r) => <li key={r.label} style={{ fontSize: 13 }}><Text type="secondary">{r.label}: {r.text}</Text></li>)}</ul>}
        {d.chainStop && <div><Text type="secondary" style={{ fontSize: 13 }}>{d.chainStop}</Text></div>}
      </div>,
    );
  }
  if (d.rejected.length) blocks.push(<div key="rej"><Text strong style={{ fontSize: 13 }}>Ý kiến bị loại ({d.rejected.length})</Text><ul style={{ margin: '4px 0 8px', paddingLeft: 18 }}>{d.rejected.map((r, i) => <li key={i} style={{ fontSize: 13 }}><Text type="secondary">{r.view.where} · {r.view.codeLabel} — {r.reasonText}</Text></li>)}</ul></div>);
  const foot = [d.modelText, d.callsText].filter(Boolean).join(' · ');
  if (foot) blocks.push(<div key="foot"><Text type="secondary" style={{ fontSize: 13 }}>{foot}</Text></div>);
  return (
    <div style={{ marginBottom: 8 }} data-testid="critic-receipt" data-status={card.status}>
      <div>
        <Pill tone={TONE_PILL[card.tone]}>{PILL_LABEL[card.status]}</Pill>{' '}
        <Text strong style={{ fontSize: 13 }} data-testid="critic-receipt-headline">{card.headline}</Text>
      </div>
      {card.detail && <div><Text type="secondary" style={{ fontSize: 13 }}>{card.detail}</Text></div>}
      <div><Text type="secondary" style={{ fontSize: 12 }} data-testid="critic-receipt-meta">{card.meta}</Text></div>
      {card.freshnessNote && (
        <div data-testid="critic-receipt-stale" style={{ marginTop: 4 }}>
          <Text type="secondary" style={{ fontSize: 13 }}>⚠ {card.freshnessNote}</Text>
          {card.staleFindings.length > 0 && <FindingList items={card.staleFindings} testId="critic-receipt-stale-list" />}
        </div>
      )}
      {/* 3h4d1: đường «Mở bước ③ / Mở cảnh» hiện NGAY (không nằm trong khối gập) cho lỗi còn lại đầu tiên; từng lỗi còn lại có dòng riêng trong «Chi tiết biên lai». */}
      {d.remaining.length > 0 && (() => { const first = d.remaining[0]; const line = traceLineOf(trace, first.sceneId); const sid = first.sceneId; return line
        ? <LocalizeTraceNote line={line} testId="critic-trace-top" onOpenStage={onOpenStage} onOpenScene={sid && onOpenScene ? () => onOpenScene(sid) : undefined} /> : null; })()}
      {blocks.length > 0 && (
        <Collapse
          size="small" ghost style={{ marginTop: 2 }} defaultActiveKey={openDetails ? ['receipt'] : undefined}
          items={[{ key: 'receipt', label: <Text type="secondary" style={{ fontSize: 13 }}>Chi tiết biên lai</Text>, children: <div data-testid="critic-receipt-details">{blocks}</div> }]}
        />
      )}
    </div>
  );
};

export default CriticReceiptCard;
